import { useState } from "react";
import { useTranslation } from "react-i18next";

import BaseSkeleton from "@/components_v2/Skeleton/BaseSkeleton";
import BaseSkeletonGroup from "@/components_v2/Skeleton/BaseSkeletonGroup";
import { SKELETON_VARIANTS } from "@/components_v2/Skeleton/constants";
import { useGetAdminContributionGradesQuery } from "@/redux/service";
import { isPaginatedResponse } from "@/redux/typeGuards";
import FlagFilters from "@/features/certifications/community-flags/components/FlagFilters";
import GradeCard from "@/features/certifications/community-flags/components/GradeCard";
import NotCountedModal from "@/features/certifications/community-flags/components/NotCountedModal";
import {
  ALL,
  FLAG_SKELETON_CARDS,
} from "@/features/certifications/community-flags/constants";
import {
  filterGrades,
  sortOldestFirst,
} from "@/features/certifications/community-flags/utils";
import {
  CommunityFlagsTabProps,
  TypeFilter,
  VerdictFilter,
} from "@/features/certifications/community-flags/types";
import { CONTRIBUTION_GRADE_STATES } from "@/domains/certifications/constants";

import "./styles.css";

const CommunityFlagsTab = ({ certification }: CommunityFlagsTabProps) => {
  const { t } = useTranslation();
  const [type, setType] = useState<TypeFilter>(ALL);
  const [verdict, setVerdict] = useState<VerdictFilter>(ALL);
  const [isNotCountedOpen, setIsNotCountedOpen] = useState(false);
  const [feedback, setFeedback] = useState<Record<number, string>>({});

  const pending = useGetAdminContributionGradesQuery({
    certification: certification.id,
    state: CONTRIBUTION_GRADE_STATES.PENDING_HUMAN_REVIEW,
    pagination: "off",
  });
  const rejected = useGetAdminContributionGradesQuery({
    certification: certification.id,
    state: CONTRIBUTION_GRADE_STATES.REJECTED,
    pagination: "off",
  });

  const listOf = (data: typeof pending.data) =>
    data ? (isPaginatedResponse(data) ? data.results : data) : [];
  const queue = listOf(pending.data);
  const notCounted = listOf(rejected.data);
  const shown = sortOldestFirst(filterGrades(queue, type, verdict));

  const handleTypeChange = (value: TypeFilter) => {
    setType(value);
    setVerdict(ALL);
  };

  const handleFeedbackChange = (gradeId: number, value: string) =>
    setFeedback((previous) => ({ ...previous, [gradeId]: value }));

  let content;
  if (pending.isLoading) {
    content = (
      <BaseSkeletonGroup
        label={t("labels.loading")}
        className="flex flex-col gap-300"
      >
        {Array.from({ length: FLAG_SKELETON_CARDS }, (_, index) => (
          <div
            key={index}
            className="flag-card flag-card-body flex flex-col gap-300"
          >
            <div className="flex items-center gap-200">
              <BaseSkeleton
                variant={SKELETON_VARIANTS.CIRCLE}
                width="var(--spacing-600)"
                height="var(--spacing-600)"
              />

              <BaseSkeleton width="10rem" />
            </div>

            <BaseSkeleton variant={SKELETON_VARIANTS.TEXT} lines={3} />
          </div>
        ))}
      </BaseSkeletonGroup>
    );
  } else if (pending.isError) {
    content = (
      <p
        className="flags-message flex justify-center body-m-regular"
        role="alert"
      >
        {t("certification.admin.communityFlags.error")}
      </p>
    );
  } else if (shown.length === 0) {
    const isFiltered = queue.length > 0;
    content = (
      <div className="flags-empty flex flex-col items-center gap-100">
        <span className="body-m-medium">
          {t(
            isFiltered
              ? "certification.admin.communityFlags.empty.filteredTitle"
              : "certification.admin.communityFlags.empty.title",
          )}
        </span>

        <span className="body-s-regular">
          {t(
            isFiltered
              ? "certification.admin.communityFlags.tryAll"
              : "certification.admin.communityFlags.empty.body",
          )}
        </span>
      </div>
    );
  } else {
    content = (
      <div
        className={`flex flex-col gap-300 ${pending.isFetching ? "flags-refreshing" : ""}`}
        aria-busy={pending.isFetching}
      >
        {shown.map((grade) => (
          <GradeCard
            key={grade.id}
            certification={certification}
            grade={grade}
            feedback={feedback[grade.id] ?? ""}
            onFeedbackChange={handleFeedbackChange}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-400">
      <FlagFilters
        type={type}
        verdict={verdict}
        notCountedCount={notCounted.length}
        onTypeChange={handleTypeChange}
        onVerdictChange={setVerdict}
        onShowNotCounted={() => setIsNotCountedOpen(true)}
      />

      {content}

      <NotCountedModal
        open={isNotCountedOpen}
        certification={certification}
        grades={notCounted}
        onClose={() => setIsNotCountedOpen(false)}
      />
    </div>
  );
};

export default CommunityFlagsTab;
